import { type ItemTemplate, upgradeColor } from '@shefira/shared';

import type { CssAtlas } from '../assets/dom-atlas.js';
import { uiText } from './text.js';
import './upgrade.css';

/** Every item shows in a box of this side, whatever its icon's own size. */
export const ICON_BOX = 36;

/**
 * Paint an item's icon on a DOM element: the packed atlas as a CSS
 * background, shown at its native scale so the pixels stay square. Icons are
 * trimmed to their content and so differ in size: the box stays fixed and
 * the frame is centred inside it, on whole pixels, rather than letting a wide
 * icon push out of its slot. Without an icon, the first letters stand in.
 */
export function paintItemIcon(
  element: HTMLElement,
  template: ItemTemplate | undefined,
  atlas: CssAtlas | null,
  fallback: string,
): void {
  const rect = template === undefined || atlas === null ? null : atlas.rect(template.icon);
  if (rect !== null && atlas !== null) {
    element.classList.add('item--icon');
    const offsetX = Math.round((ICON_BOX - rect.width) / 2);
    const offsetY = Math.round((ICON_BOX - rect.height) / 2);
    element.style.backgroundImage = `url('${atlas.url}')`;
    element.style.backgroundPosition = `${String(offsetX - rect.x)}px ${String(offsetY - rect.y)}px`;
    element.style.backgroundSize = `${String(atlas.width)}px ${String(atlas.height)}px`;
    return;
  }
  element.textContent = uiText(template?.name ?? fallback)
    .slice(0, 3)
    .toUpperCase();
}

/**
 * The green arrow in the top-right corner of a piece better than the one worn
 * (`isBetterThanWorn`): the « +N » takes the other top corner, the count the
 * bottom one.
 */
export function addBetterArrow(element: HTMLElement): void {
  const arrow = document.createElement('span');
  arrow.className = 'item__better';
  arrow.setAttribute('role', 'img');
  arrow.setAttribute('aria-label', uiText('Mieux que ce que tu portes'));
  element.append(arrow);
}

/** The smith's « +N » in the corner of an item, in its level's colour from +3; nothing at +0. */
export function addLevelBadge(element: HTMLElement, level: number): void {
  if (level <= 0) return;
  const badge = document.createElement('span');
  badge.className = 'item__level';
  const color = upgradeColor(level);
  if (color !== undefined) badge.style.color = `#${color.toString(16).padStart(6, '0')}`;
  badge.textContent = `+${String(level)}`;
  element.append(badge);
}
